// Client twin of backend/shared/docStructure.js textBlocks(): split clause
// text into paragraphs and markdown pipe tables so structured documents
// (FDDs, specs) render real tables in the app exactly as they export to Word.
const isTableLine = (line) => /^\s*\|.*\|\s*$/.test(line);
const isDividerLine = (line) => /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/.test(line);
const splitRow = (line) =>
  line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

export function textBlocks(text) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let para = [];
  let table = null;

  const flushPara = () => {
    const t = para.join('\n').trim();
    para = [];
    if (t) blocks.push({ type: 'p', text: t });
  };
  const flushTable = () => {
    if (table && table.rows.length) blocks.push(table);
    table = null;
  };

  for (const line of lines) {
    if (isTableLine(line)) {
      flushPara();
      if (!table) table = { type: 'table', rows: [], header: false };
      if (isDividerLine(line)) {
        if (table.rows.length === 1) table.header = true;
        continue;
      }
      table.rows.push(splitRow(line));
    } else {
      flushTable();
      para.push(line);
    }
  }
  flushPara();
  flushTable();
  return blocks;
}

export const hasTable = (text) => /^\s*\|.*\|\s*$/m.test(String(text || ''));

// Slide body parser (deck rendering) — bullets ("- ", 2-space indents),
// paragraphs, pipe tables, and "Notes:"-prefixed speaker notes. Client twin
// of backend/shared/deckExport.js parseSlideBody.
export function parseSlideBody(text) {
  const notesSplit = String(text || '').split(/^Notes:\s*/m);
  const body = notesSplit[0];
  const notes = notesSplit.slice(1).join('\n').trim();
  const bullets = [];
  const paras = [];
  const tables = [];
  for (const block of textBlocks(body)) {
    if (block.type === 'table') { tables.push(block); continue; }
    for (const rawLine of block.text.split('\n')) {
      const m = rawLine.match(/^(\s*)[-*]\s+(.*)$/);
      if (m) bullets.push({ text: m[2].trim(), indent: Math.min(3, Math.floor(m[1].length / 2)) });
      else if (rawLine.trim()) paras.push(rawLine.trim());
    }
  }
  return { bullets, paras, tables, notes };
}

const snapshot = (c) => c && {
  heading: c.heading,
  text: c.text,
  ...(c.level !== undefined ? { level: c.level } : {}),
  ...(c.layout !== undefined ? { layout: c.layout } : {}),
};

// Apply drafting-agent ops to a clause list. Returns the new clause list AND
// the changes manifest ops — full before/after snapshots per change, so any
// published version is traceable (and individually revertible) later.
export function applyOpsWithManifest(clauses, ops) {
  let out = clauses.map((c) => ({ ...c }));
  const manifestOps = [];
  let seq = out.length;
  for (const op of ops || []) {
    if (op.op === 'edit') {
      const prev = out.find((c) => c.id === op.id);
      if (!prev) continue;
      out = out.map((c) => (c.id === op.id
        ? {
          ...c,
          ...(op.text !== undefined ? { text: op.text } : {}),
          ...(op.heading !== undefined ? { heading: op.heading } : {}),
          ...(op.level !== undefined ? { level: op.level } : {}),
          ...(op.layout !== undefined ? { layout: op.layout } : {}),
        }
        : c));
      manifestOps.push({ op: 'edit', id: op.id, before: snapshot(prev), after: snapshot(out.find((c) => c.id === op.id)) });
    } else if (op.op === 'remove') {
      const i = out.findIndex((c) => c.id === op.id);
      if (i < 0) continue;
      manifestOps.push({
        op: 'remove',
        id: op.id,
        afterId: i > 0 ? out[i - 1].id : null, // where it lived — lets a revert re-insert in place
        before: snapshot(out[i]),
        after: null,
      });
      out.splice(i, 1);
    } else if (op.op === 'add' && op.section) {
      seq += 1;
      const section = { id: `c${Date.now().toString(36)}${seq}`, ...op.section };
      let i;
      if (!op.afterId) { out.unshift(section); i = 0; }
      else {
        i = out.findIndex((c) => c.id === op.afterId);
        i = i < 0 ? out.length : i + 1;
        out.splice(i, 0, section);
      }
      manifestOps.push({ op: 'add', id: section.id, afterId: op.afterId || null, before: null, after: snapshot(section) });
    }
  }
  return { clauses: out, manifestOps };
}

export const applyOps = (clauses, ops) => applyOpsWithManifest(clauses, ops).clauses;

// Invert one manifest op → the agent-style op that undoes it against the
// CURRENT draft (id-based, so it survives intervening versions).
export function invertManifestOp(mOp) {
  if (mOp.op === 'add') return { op: 'remove', id: mOp.id };
  if (mOp.op === 'remove') return { op: 'add', afterId: mOp.afterId || null, section: { ...mOp.before } };
  return { op: 'edit', id: mOp.id, ...mOp.before };
}

// Can this manifest op still be reverted against the given clauses?
export function canRevert(mOp, clauses) {
  const exists = clauses.some((c) => c.id === mOp.id);
  if (mOp.op === 'add' || mOp.op === 'edit') return exists;
  return !exists; // remove: only revertible if it hasn't been re-added
}
